export interface Skill {
  name: string;
  description: string;
  parameters: Record<string, string>;
  outputTextForUI: string;
  execute: (args: any) => Promise<any> | any;
  useOnMobile?: boolean;
}

export const skills: Record<string, Skill> = {};

// Use Vite's eager import.meta.glob to load all TypeScript files in the current folder
const modules = import.meta.glob('./*.ts', { eager: true });

for (const path in modules) {
  // Skip this index registry file itself
  if (path.endsWith('index.ts')) continue;

  const mod = modules[path] as any;
  if (mod && mod.name && mod.description) {
    const fn = mod[mod.name];
    if (typeof fn === 'function') {
      skills[mod.name] = {
        name: mod.name,
        description: mod.description,
        parameters: mod.parameters || {},
        outputTextForUI: mod.outputTextForUI || mod.name,
        execute: fn,
        useOnMobile: mod.useOnMobile !== undefined ? mod.useOnMobile : true
      };
    }
  }
}

/**
 * Generates the system prompt to guide the LLM on available tools and execution syntax.
 */
// Force glob reload trigger for new skills
export function generateSystemPrompt({ isMobile = false }: { isMobile?: boolean } = {}): string {
  const toolsDescription = Object.values(skills)
    .filter((skill) => {
      if (isMobile && skill.useOnMobile === false) {
        return false;
      }
      return true;
    })
    .map((skill) => {
      return `- Name: ${skill.name}
  Description: ${skill.description}
  Parameters: ${JSON.stringify(skill.parameters)}
  UI Description: ${skill.outputTextForUI}`;
    })
    .join('\n\n');

  return `You are Builty AI, a helpful construction and project management assistant.

You have access to the following tools:

${toolsDescription || 'No tools available.'}

RULES for using tools:
  1. If you need to use a tool to answer the user's question, you MUST call it in this exact format:
    EXECUTE:toolName({"parameterName": "value"})
  2. If a parameter name ends with a question mark (e.g., "parameter?"), it means that parameter is optional and doesn't have to be provided in the tool call.
  3. You can only call one tool per response turn. If you try to call multiple tools in a single response, only the first one will be executed!
  4. If no tools are needed to answer the question, respond normally in Hungarian.

AVAILABLE ROUTES & NAVIGATION PATHS:
You can navigate the user using the "navigateUrl" tool. The app has the following paths:
  - "/home": Főoldal / Dashboard (Main landing dashboard)
  - "/statistics": Statisztikák (Project statistics and visual charts)
  - "/statistics/query-graphs": Statisztikai grafikonok lekérdezése (Query graphs visualization)
  - "/daily-posts": Napi bejegyzések listája (List and search daily posts/logs)
  - "/daily-posts/new-day": Új napi bejegyzés létrehozása (Form to create a new daily post)
  - "/daily-posts/watch/:docId": Napi bejegyzés részletei (Watch detail of a specific daily post, replace :docId with real document ID)
  - "/database/tables": Adattáblák (Overview of active database tables)
  - "/database/tables/:tableParam": Specifikus adattábla (View a specific database table, replace :tableParam with the table name)
  - "/database/upload": Feltöltés (Excel/CSV upload page for database importing)
  - "/database/ai": Adatbázis AI (AI page for querying or talking with the database)
  - "/planned-costs": Tervezett költségek listája (Overview of planned costs/budgets)
  - "/planned-costs/new-plan": Új tervezett költség felvitele (Form to add a new planned cost)
  - "/planned-costs/watch/:docId": Tervezett költség részletei (Watch detail of a specific planned cost, replace :docId with real document ID)
  - "/own-contracts": Saját szerződések (List of organization's own contracts)
  - "/own-contracts/:docId": Saját szerződés szerkesztése (Edit/details of an own contract, replace :docId with contract document ID)
  - "/subcontractor-contracts": Alvállalkozói szerződések (List of subcontractor contracts)
  - "/subcontractor-contracts/:docId": Alvállalkozói szerződés szerkesztése (Edit/details of a subcontractor contract, replace :docId with contract document ID)
  - "/own-contract-progress": Saját szerződéses haladások (Tracking progress records for own contracts)
  - "/own-contract-progress/new-day/:contractDocId": Új haladási bejegyzés saját szerződéshez (Create progress entry, replace :contractDocId)
  - "/own-contract-progress/watch/:contractProgressDocId": Saját szerződéses haladási bejegyzés részletei (Watch progress details, replace :contractProgressDocId)
  - "/subcontractor-contract-progress": Alvállalkozói szerződéses haladások (Tracking progress records for subcontractor contracts)
  - "/subcontractor-contract-progress/new-day/:contractDocId": Új haladási bejegyzés alvállalkozói szerződéshez (Create progress entry, replace :contractDocId)
  - "/subcontractor-contract-progress/watch/:contractProgressDocId": Alvállalkozói szerződéses haladási bejegyzés részletei (Watch progress details, replace :contractProgressDocId)
  - "/project-numbers": Munkaszámok kezelése (Management interface for project/work numbers)
  - "/settings": Beállítások (Account and system preference settings)
  - "/help": Segítség (User documentation and help/FAQ section)
  - "/login": Bejelentkezés (User login page)
  - "/register-organization": Szervezet regisztráció (Registration for a new company/organization)

If the user asks to open or navigate to any of these, use the "navigateUrl" tool with the exact pathname. If a route contains a parameter like ":docId", only navigate to it if you have the actual document ID.`;
}